import React from 'react';
import type { Editor } from 'ckeditor5';
import { observer } from 'mobx-react';
import { FormattedMessage, useIntl } from 'react-intl';
import { CommonModal } from '../../../../../../../global/modal/common/common-modal';
import { FormField } from '../../../../../../../global/form/form-field/form-field';
import { RichTextEditor } from '../../../../../../../global/rich-text-editor';
import DefaultButton from '../../../../../../../global/default-button/default-button';
import {
    ADD_TEXT,
    APPLY_TEXT,
    CANCEL_TEXT,
    REQUIRED_FIELD_TEXT,
} from '../../../../../../../../constants/common-labels.constants';
import { EnablementStatus } from '../../../../../../../../constants/common.constants';
import {
    EDIT_RICH_TEXT_MODULE_MODAL_TITLE_TEXT,
    MODULE_ACTIVE_LABEL_TEXT,
    MODULE_CONTENT_FIELD_LABEL_TEXT,
    MODULE_LABEL_DISPLAYED_LABEL_TEXT,
    MODULE_LABEL_FIELD_LABEL_TEXT,
    NEW_RICH_TEXT_MODULE_MODAL_TITLE_TEXT,
} from '../../../../../../event-hubs.text';
import { RichTextModuleVM } from './rich-text-module.viewmodel';
import { CONTENT, DISPLAY_LABEL, LABEL } from './rich-text-module.config';
import { FormFieldInput } from '../../../../../../../global/form/form-field-input/form-field-input';
import './rich-text-module-modal.scss';

type RichTextModuleModalProps = {
    vm: RichTextModuleVM;
    isNew: boolean;
    onCancel: () => void;
    onConfirm: () => void;
};

export const RichTextModuleModal: React.FC<RichTextModuleModalProps> = observer(props => {
    const { vm, isNew, onCancel, onConfirm } = props;
    const { formatMessage } = useIntl();

    const labelDisplayedField = vm.getFormVmValuePropsByKey(DISPLAY_LABEL);
    const contentField = vm.getFormVmValuePropsByKey(CONTENT);

    const titleDescriptor = isNew ? NEW_RICH_TEXT_MODULE_MODAL_TITLE_TEXT : EDIT_RICH_TEXT_MODULE_MODAL_TITLE_TEXT;
    const confirmDescriptor = isNew ? ADD_TEXT : APPLY_TEXT;

    const handleActiveToggle = () => vm.setEnabled(!vm.isEnabled());
    const handleLabelDisplayedToggle = () => labelDisplayedField.setValue(!labelDisplayedField.value);
    const handleContentChange = (_: unknown, editor: Editor) => contentField.setValue(editor.getData());
    const hasContentError = !contentField.valid && contentField.touched;

    return (
        <CommonModal
            isOpen
            title={<FormattedMessage {...titleDescriptor} />}
            onRequestClose={onCancel}
            className='rich-text-module-modal'
            footer={
                <div className='rich-text-module-modal__footer'>
                    <DefaultButton
                        modifier='cancel'
                        text={<FormattedMessage {...CANCEL_TEXT} />}
                        onClick={onCancel}
                    />
                    <DefaultButton
                        text={<FormattedMessage {...confirmDescriptor} />}
                        onClick={onConfirm}
                    />
                </div>
            }
        >
            <div className='rich-text-module-modal__body'>
                <FormFieldInput.Toggle
                    isSelected={vm.isEnabled()}
                    toggle={handleActiveToggle}
                    text={<FormattedMessage
                        values={{ suffix: vm.isEnabled() ? EnablementStatus.ENABLED : EnablementStatus.DISABLED }}
                        {...MODULE_ACTIVE_LABEL_TEXT}
                    />}
                    className='rich-text-module-modal__toggle-row'
                />

                <div className='rich-text-module-modal__label-row'>
                    <FormFieldInput.Text
                        field={vm.getFormVmValuePropsByKey(LABEL)}
                        labelDescriptor={MODULE_LABEL_FIELD_LABEL_TEXT}
                        required
                        additionalClassName='rich-text-module-modal__label-input'
                        withError
                    />

                    <FormFieldInput.Toggle
                        isSelected={labelDisplayedField.value as boolean}
                        toggle={handleLabelDisplayedToggle}
                        text={<FormattedMessage
                            values={{ suffix: labelDisplayedField.value ? EnablementStatus.DISPLAYED : EnablementStatus.NOT_DISPLAYED }}
                            {...MODULE_LABEL_DISPLAYED_LABEL_TEXT}
                        />}
                        className='rich-text-module-modal__toggle-row rich-text-module-modal__toggle-row--inline'
                    />
                </div>

                <FormField
                    className='m-bottom-20px'
                    label={<>
                        <FormattedMessage {...MODULE_CONTENT_FIELD_LABEL_TEXT} />
                        <span className='oe-form-field__title-required'><FormattedMessage {...REQUIRED_FIELD_TEXT} /></span>
                    </>}
                >
                    <RichTextEditor
                        className='oe-form-field__control m-0px p-0px'
                        resizable
                        value={contentField.value as string || ''}
                        onChange={handleContentChange}
                        onBlur={contentField.setTouched}
                        validationError={
                            hasContentError &&
                            <FormattedMessage
                                values={{ fieldName: formatMessage(MODULE_CONTENT_FIELD_LABEL_TEXT) }}
                                {...contentField.validationMessage}
                            />
                        }
                    />
                </FormField>
            </div>
        </CommonModal>
    );
});
